import Constants from '@/constants'
import {
  buildInstruction,
  buildRequest,
  isCapitecPayAcquirer,
  isNedbankPayAcquirer,
  isOpenBankingAcquirer,
  isNotUndefinedOrNull,
} from '@/utils/index'
import store from '../store'
import NotificationEvent from '@/events/notificationEvents'
import LoadingManager from '@/LoadingManager'
import EventBus from '@/EventBus.js'
import SidMessage from '@/dto/SidMessage'

export async function connectSignalR(transactionId) {
  try {
    const systemName = process.env.VUE_APP_NAME
    await this.$connectSignalR(transactionId, systemName)
    this.$logInfo({
      transactionId: transactionId,
      message: Constants.logMessages.signalRConnected,
    })
  } catch (error) {
    this.$handleException({ error })
  }
}

export function receiveRequest(
  messageId,
  message,
  requestType,
  options,
  transactionId
) {
  const sidMessage = {
    identifier: messageId,
    message,
    requestType,
    options,
  }

  const request = buildRequest(sidMessage, transactionId)
  if (transactionIdMismatch(transactionId, this.transactionId)) {
    this.$logException(
      generateTransactionIdMismatchException(request, this.transactionId)
    )
    return
  }

  store.dispatch('signalRMessage/setRequest', request)

  this.sendAcknowledgement(messageId)
}

export function receiveInstruction(
  messageId,
  message,
  instructionType,
  options,
  transactionId
) {
  const sidMessage = {
    identifier: messageId,
    message,
    instructionType,
    options,
    transactionId,
  }

  const instruction = buildInstruction(sidMessage, transactionId)
  if (transactionIdMismatch(transactionId, this.transactionId)) {
    this.$logException(
      generateTransactionIdMismatchException(instruction, this.transactionId)
    )
    return
  }

  const payload = SafelyParseJSON(instruction.options)
  if (isOpenBankingAcquirer(payload?.AcquirerCode)) {
    const request = {
      messageId: instruction.messageId,
      message: '',
      requestType: Constants.requestTypes.BANKSELECT,
      transactionId: instruction.transactionId,
      isProcessing: false,
    }

    if (isCapitecPayAcquirer(payload.AcquirerCode)) {
      handleCapitecPayInstruction(instruction, request)
    } else if (isNedbankPayAcquirer(payload.AcquirerCode)) {
      EventBus.$emit('instruction-received', instruction)
      handleNedbankPayInstruction(instruction, payload)
    }
  }

  //need to invesigate what happens if we only do this if non scraping
  store.dispatch('signalRMessage/setInstruction', instruction)
  this.sendAcknowledgement(messageId)
}

export function receiveSidMessage(messageId, message) {
  const sidMessage = new SidMessage({ ...message, Identifier: messageId })

  if (transactionIdMismatch(sidMessage.transactionId, this.transactionId)) {
    this.$logException(
      generateTransactionIdMismatchException(sidMessage, this.transactionId)
    )
    return
  }

  switch (sidMessage.options.AcquirerCode) {
    case Constants.acquirerCodes.AbsaPay:
      handleAbsaPayTransaction(sidMessage)
      break
    default:
    // Unknown/Unexpected acquirer code - throw error
  }
}

function SafelyParseJSON(json) {
  // This function cannot be optimised, it's best to
  // keep it small!
  var parsed

  try {
    parsed = JSON.parse(json)
  } catch (e) {
    // Oh well, but whatever...
  }

  return parsed // Could be undefined!
}

export function checkSignalRConnection() {
  this.checkSignalRConnectionIntervalId = setInterval(async () => {
    try {
      await this.$signalRHubProxy.invoke('keepAlive')
    } catch (err) {
      window.location.reload()
    }
  }, 5000)
}

export async function joinGroup(transactionId) {
  try {
    await this.$signalRHubProxy.invoke('joinGroup', transactionId)
    this.$logInfo({
      transactionId,
      message: Constants.logMessages.joinedSignalRGroup(transactionId),
    })
  } catch (error) {
    this.$handleException({ error })
  }
}

export function sendInstruction(
  groupName,
  message,
  instructionType,
  userInputID
) {
  return this.$signalRHubProxy.invoke(
    'sendInstruction',
    groupName,
    message,
    instructionType,
    null,
    true,
    userInputID
  )
}

export function sendAcknowledgement(msgId) {
  return this.$signalRHubProxy.invoke('sendAcknowledgement', msgId)
}

export function sendResponse({ messageId, instruction }) {
  return this.$signalRHubProxy.invoke('sendResponse', messageId, instruction)
}

/**
 * Generates an object for TNX ID mismatch
 * @param {object} data Data received from SignalR
 * @param {string} urlTransactionId The ID of the transaction from the URL
 */
function generateTransactionIdMismatchException(data, urlTransactionId) {
  let dataStringified
  try {
    dataStringified = JSON.stringify(data)
  } catch (error) {
    dataStringified = 'Data could not be parsed!'
  }

  return {
    transactionId: urlTransactionId,
    message: `Transaction ID from SignalR does not match URL! 
    Expected ${urlTransactionId}, Actual: ${data.transactionId}
    Data Received From SignalR: ${dataStringified}`,
  }
}

/**
 * Returns if the ID of the transaction in SignalR matches the
 * transaction ID from the URL.
 * @param {string} signalRTransactionId The ID of the transaction from SignalR
 * @param {string} urlTransactionId The ID of the transaction from the URL
 */
function transactionIdMismatch(signalRTransactionId, urlTransactionId) {
  // There's a chance our API hasn't been updated yet
  // to give us the TNX ID back, in that case, just
  // return false.
  if (!signalRTransactionId) {
    return false
  }

  return urlTransactionId !== signalRTransactionId
}

function handleCapitecPayInstruction(instruction, request) {
  const payload = JSON.parse(instruction.options)
  switch (payload.Action) {
    case Constants.bankApiActionTypes.CREATE:
      if (
        isNotUndefinedOrNull(payload.StatusCode) &&
        payload.StatusCode == 'Cancel'
      ) {
        instruction.instructionType = Constants.instructionTypes.CANCEL
        //we do not want to modify the request..
        return
      }

      if (payload.Success) {
        request.requestType = Constants.requestTypes.BANKAUTHTIMER
      } else {
        request.requestType = Constants.requestTypes.CREDENTIALS
        request.message = getBankApiErrorMessage(payload)
        instruction.instructionType = Constants.instructionTypes.MESSAGE
      }
      store.dispatch('signalRMessage/setRequest', request)
      break
    case Constants.bankApiActionTypes.STATUS:
      switch (payload.StatusCode) {
        case 'Cancel':
          instruction.instructionType = Constants.instructionTypes.CANCEL
          break
        case 'Completed':
          instruction.instructionType = Constants.instructionTypes.COMPLETED
          break
        default:
          break
      }
      break
    default:
      // to do - throw error
      break
  }

  if (!!instruction && !!instruction.options) {
    // eslint-disable-next-line no-prototype-builtins
    if (!!payload && payload.hasOwnProperty('TimeToLive')) {
      store.dispatch('transaction/setTimeToLive', payload.TimeToLive)
    }
  }
}

function handleNedbankPayInstruction(instruction, payload) {
  LoadingManager.setLoading(false)
  if (!payload.Success) {
    //we can be on progress view... we need to go back to a view which displays error and allows retry.
    instruction.instructionType = Constants.instructionTypes.CANCEL

    //request.requestType = Constants.requestTypes.CREDENTIALS
    //request.message = getBankApiErrorMessage(payload)
    //not sure about the below line
    //instruction.instructionType = Constants.instructionTypes.MESSAGE
    //    store.dispatch('signalRMessage/setRequest', request)
    // console.log(
    //   'handleNedbankPayInstruction -fail:' + getBankApiErrorMessage(payload)
    // )
    // NotificationEvent.setNotification(
    //   getBankApiErrorMessage(payload),
    //   'err-msg'
    // )
  }

  if (payload.Action) {
    switch (payload.Action) {
      case 'Initiate':
        if (payload?.RedirectUrl) {
          window.location.replace(payload.RedirectUrl)
        }
        break
      case 'SubmitPayment':
        if (payload.StatusCode == 'Cancel') {
          instruction.instructionType = Constants.instructionTypes.CANCEL
          return
        }
        break
      case 'Status':
        switch (payload.StatusCode) {
          case 'Cancel':
            instruction.instructionType = Constants.instructionTypes.CANCEL
            break
          case 'Completed':
            instruction.instructionType = Constants.instructionTypes.COMPLETED
            break
          default:
            break
        }
        break
      default:
        // to do - throw error
        break
    }
  }
}

async function handleAbsaPayTransaction(sidMessage) {
  if (!sidMessage.options.Success) {
    if (sidMessage.isCancelled()) {
      await store.dispatch(
        'signalRMessage/setInstruction',
        sidMessage.getInstruction()
      )
    }

    NotificationEvent.setNotification(
      getBankApiErrorMessage(sidMessage.options),
      'err-msg'
    )

    LoadingManager.setLoading(sidMessage.isProcessing)
    return
  }

  if (sidMessage.requestType)
    await store.dispatch('signalRMessage/setRequest', sidMessage.getRequest())

  if (sidMessage.instructionType && !sidMessage.isBehaviourNone())
    await store.dispatch(
      'signalRMessage/setInstruction',
      sidMessage.getInstruction()
    )

  if (sidMessage.isMessage()) {
    LoadingManager.setMessage(sidMessage.message)
  }
  LoadingManager.setLoading(sidMessage.isProcessing)

  await store.dispatch('signalRMessage/setSidMessage', sidMessage)
}

function getBankApiErrorMessage(meta) {
  let message = 'Login failed. Please try again later.'
  if (meta) {
    if (meta.ErrorMessages && meta.ErrorMessages.length)
      message = meta.ErrorMessages[0].ErrorMessage
  }
  return message
}
